/**
 * Game URL utilities for handling RTP variant masking
 * Provides clean URLs without exposing RTP suffixes to users
 */

/**
 * Get clean game code by removing potential variant suffix
 * @param {string} gameCode - Original game code
 * @returns {string} Clean game code without potential variant suffix
 */
export function getCleanGameCode(gameCode) {
  if (!gameCode || typeof gameCode !== "string") {
    return gameCode;
  }

  // Remove potential variant suffixes (2 digits OR 2 letters at end)
  // The backend validates whether this is actually a variant by checking if the base game exists
  // This handles cases like:
  // - gco_xmasunwrapped92 → gco_xmasunwrapped (if base game exists)
  // - mg_slots_immortalromanceiiVF → mg_slots_immortalromanceii (if base game exists)
  // - gco_penaltychampion294 → gco_penaltychampion2 (removes 94, keeps 2 as part of name)
  // - some_gameAB → some_game (removes AB, backend validates if some_game exists)
  return gameCode.replace(/(\d{2}|[A-Z]{2})$/, "");
}

/**
 * Generate clean game URL for display purposes
 * @param {Object} game - Game object
 * @param {string} game.game_code - Game code
 * @param {string} game.variant_group_id - Variant group ID (optional)
 * @param {string} currency - Currency ('CB' or 'GLD')
 * @returns {string} Clean game URL
 */
export function generateGameUrl(game, currency = "CB") {
  if (!game || !game.game_code) {
    return "/games";
  }

  let gameCode = game.game_code;

  // If this game is part of a variant group, use clean code
  if (game.variant_group_id) {
    gameCode = getCleanGameCode(game.game_code);
  }

  // Generate URL based on currency
  if (currency === "GLD") {
    return `/games/${gameCode}/fun`;
  } else {
    return `/games/${gameCode}`;
  }
}

/**
 * Generate clean game URL for CB currency (default)
 * @param {Object} game - Game object
 * @returns {string} Clean game URL for CB currency
 */
export function generateCBGameUrl(game) {
  return generateGameUrl(game, "CB");
}

/**
 * Generate clean game URL for GLD currency (fun mode)
 * @param {Object} game - Game object
 * @returns {string} Clean game URL for GLD currency
 */
export function generateGLDGameUrl(game) {
  return generateGameUrl(game, "GLD");
}

/**
 * Check if a game code has a potential variant suffix
 * @param {string} gameCode - Game code to check
 * @returns {boolean} True if game code ends with 2 digits or 2 letters
 */
export function hasVariantSuffix(gameCode) {
  if (!gameCode || typeof gameCode !== "string") {
    return false;
  }

  return /(\d{2}|[A-Z]{2})$/.test(gameCode);
}

/**
 * Get the potential variant suffix from a game code
 * @param {string} gameCode - Game code
 * @returns {string|null} Variant suffix or null if none found
 */
export function getVariantSuffix(gameCode) {
  if (!gameCode || typeof gameCode !== "string") {
    return null;
  }

  const match = gameCode.match(/(\d{2}|[A-Z]{2})$/);
  return match ? match[1] : null;
}

/**
 * Check if two game codes are variants of the same base game
 * @param {string} gameCode1 - First game code
 * @param {string} gameCode2 - Second game code
 * @returns {boolean} True if they are variants of the same game
 */
export function areGameVariants(gameCode1, gameCode2) {
  if (!gameCode1 || !gameCode2) {
    return false;
  }

  const clean1 = getCleanGameCode(gameCode1);
  const clean2 = getCleanGameCode(gameCode2);

  return clean1 === clean2 && clean1 !== gameCode1 && clean2 !== gameCode2;
}

/**
 * Format game URL for social sharing (always use clean URLs)
 * @param {Object} game - Game object
 * @param {string} baseUrl - Base URL of the site
 * @returns {string} Full URL for social sharing
 */
export function formatGameSocialUrl(game, baseUrl = window.location.origin) {
  const gameUrl = generateCBGameUrl(game);
  return `${baseUrl}${gameUrl}`;
}
